import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
// Removed react-pdf imports - using iframe instead
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  PlayArrow as ProcessIcon,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocumentIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Receipt as InvoicesIcon,
  ChevronRight as ChevronRightIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  Assignment as ContractIcon,
  List as ListIcon
} from '@mui/icons-material';
import { invoiceService } from '../../services/invoice.service';
import { INVOICE_API_BASE_URL, INVOICE_API_KEY } from '../../config/api.config';

// Using iframe-based PDF viewer instead of react-pdf to avoid worker issues

const DocumentDetails = ({ documentId, onBack, onProcessDocument, basketName, pdfPreviewOpen: initialPdfPreviewOpen, onPdfPreviewClose, onBackToDashboard, onBackToList }) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(initialPdfPreviewOpen || false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [originalDocumentData, setOriginalDocumentData] = useState(null);
  const [originalDocumentLoading, setOriginalDocumentLoading] = useState(false);
  const [expandedRawResponses, setExpandedRawResponses] = useState({});
  const [expandedLLMResponses, setExpandedLLMResponses] = useState({});

  const fetchOriginalDocumentData = useCallback(async (processedDocId) => {
    try {
      setOriginalDocumentLoading(true);
      
      // Get the original document ID from processed document
      const processedDoc = await invoiceService.getDocumentMetadata(processedDocId);
      const originalDocId = processedDoc.metadata?.original_document_id;
      
      if (originalDocId) {
        // Get raw responses from original document
        const rawResponses = await invoiceService.getRawResponses(originalDocId);
        
        // Get original document metadata
        const originalMetadata = await invoiceService.getDocumentMetadata(originalDocId);
        
        setOriginalDocumentData({
          originalDocId,
          rawResponses,
          metadata: originalMetadata
        });
      }
    } catch (err) {
      console.error('Failed to fetch original document data:', err);
      // Don't set error here as it's not critical for the main document display
    } finally {
      setOriginalDocumentLoading(false);
    }
  }, []);

  const fetchDocumentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get document metadata
      const metadataResponse = await invoiceService.getDocumentMetadata(documentId);
      
      // Get processing status
      const statusResponse = await invoiceService.getProcessingStatus(documentId);
      
      // Set the document data with both metadata and processing status
      setDocument({
        ...metadataResponse,
        processingStatus: statusResponse
      });

      // If this is a processed document, fetch original document data
      if (metadataResponse.metadata?.original_document_id) {
        fetchOriginalDocumentData(documentId);
      }
    } catch (err) {
      console.error('Failed to fetch document details:', err);
      setError('Failed to load document details');
    } finally {
      setLoading(false);
    }
  }, [documentId, fetchOriginalDocumentData]);

  useEffect(() => {
    if (documentId) {
      fetchDocumentDetails();
    }
  }, [documentId, fetchDocumentDetails]);

  // Handle initial PDF preview state
  useEffect(() => {
    if (initialPdfPreviewOpen) {
      setPdfPreviewOpen(true);
    }
  }, [initialPdfPreviewOpen]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'valid':
        return 'success';
      case 'failed':
      case 'invalid':
        return 'error';
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      default:
        return 'default';
    }
  };

  const getBasketColor = (basket) => {
    switch (basket) {
      case 'ready_to_pay':
        return 'success';
      case 'further_validation':
        return 'error';
      case 'processed_documents':
        return 'info';
      case 'raw_invoices':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount || amount === 'N/A') return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatLLMResponse = (llmResponse) => {
    if (!llmResponse) return 'No LLM response available';
    
    try {
      // If it's already a string, try to parse it as JSON first
      if (typeof llmResponse === 'string') {
        try {
          const parsed = JSON.parse(llmResponse);
          return JSON.stringify(parsed, null, 2);
        } catch {
          // If it's not JSON, return as is
          return llmResponse;
        }
      }
      
      // If it's an object, stringify it with formatting
      if (typeof llmResponse === 'object') {
        return JSON.stringify(llmResponse, null, 2);
      }
      
      return String(llmResponse);
    } catch (error) {
      console.error('Error formatting LLM response:', error);
      return String(llmResponse);
    }
  };

  const formatMetadataValue = (value) => {
    if (value === null || value === undefined) {
      return <em>null</em>;
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : <em>Empty array</em>;
      }
      
      // For objects, show a summary or full JSON
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <em>Empty object</em>;
      }
      
      // If it's a simple object with few keys, show inline
      if (keys.length <= 3) {
        return (
          <Box component="pre" sx={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(value, null, 2)}
          </Box>
        );
      }
      
      // For complex objects, show key count
      return `${keys.length} properties (${keys.slice(0, 2).join(', ')}${keys.length > 2 ? '...' : ''})`;
    }
    
    if (typeof value === 'string' && value.length > 100) {
      return (
        <Box>
          {value.substring(0, 100)}...
          <br />
          <em>({value.length} characters total)</em>
        </Box>
      );
    }
    
    return String(value);
  };

  const toggleRawResponseExpansion = (responseId) => {
    setExpandedRawResponses(prev => ({
      ...prev,
      [responseId]: !prev[responseId]
    }));
  };

  const toggleLLMResponseExpansion = (responseId) => {
    setExpandedLLMResponses(prev => ({
      ...prev,
      [responseId]: !prev[responseId]
    }));
  };

  const extractModelFromRawResponse = (rawResponse) => {
    if (!rawResponse) return null;
    try {
      const parsed = typeof rawResponse === 'string' ? JSON.parse(rawResponse) : rawResponse;
      return parsed.model || null;
    } catch {
      return null;
    }
  };

  const extractUsageFromRawResponse = (rawResponse) => {
    if (!rawResponse) return null;
    try {
      const parsed = typeof rawResponse === 'string' ? JSON.parse(rawResponse) : rawResponse;
      return parsed.usage || null;
    } catch {
      return null;
    }
  };

  const handlePdfLoad = () => {
    setPdfLoading(false);
    setPdfError(null);
  };

  const handlePdfError = () => {
    setPdfError('Failed to load PDF document. Please try refreshing the page or use the download button.');
    setPdfLoading(false);
  };

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfUrlLoading, setPdfUrlLoading] = useState(false);

  const getPdfUrl = useCallback(async () => {
    if (pdfBlobUrl) {
      return pdfBlobUrl;
    }

    try {
      const pdfUrl = `${INVOICE_API_BASE_URL}/api/v1/documents/${documentId}/download`;
      
      // Fetch the PDF with proper authentication
      const response = await fetch(pdfUrl, {
        headers: {
          'X-API-Key': INVOICE_API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching PDF:', error);
      throw error;
    }
  }, [documentId, pdfBlobUrl]);

  // Load PDF URL when component mounts
  useEffect(() => {
    const loadPdfUrl = async () => {
      if (documentId && !pdfBlobUrl && !pdfUrlLoading) {
        setPdfUrlLoading(true);
        try {
          await getPdfUrl();
        } catch (error) {
          console.error('Failed to load PDF URL:', error);
        } finally {
          setPdfUrlLoading(false);
        }
      }
    };

    loadPdfUrl();
  }, [documentId, pdfBlobUrl, pdfUrlLoading, getPdfUrl]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={fetchDocumentDetails}>
          Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  if (!document) {
    return (
      <Alert severity="warning">
        Document not found
      </Alert>
    );
  }

  const { metadata: docMetadata, processingStatus } = document;

  const getBasketDisplayName = (basket) => {
    switch (basket) {
      case 'raw_invoices':
        return 'Raw Documents';
      case 'processed_documents':
        return 'Processed Documents';
      case 'ready_to_pay':
        return 'Ready to Pay';
      case 'further_validation':
        return 'Needs Review';
      default:
        return basket?.replace('_', ' ').toUpperCase() || 'All Documents';
    }
  };

  const getRoutedBasketName = (metadata) => {
    if (!metadata) return null;
    
    // Check for routed basket in various possible fields
    const routedBasket = metadata.routed_to_basket || 
                        metadata.final_basket || 
                        metadata.basket_name;
    
    return routedBasket;
  };

  const getRoutedBasketDisplayName = (metadata) => {
    const routedBasket = getRoutedBasketName(metadata);
    
    if (!routedBasket) {
      return 'N/A';
    }
    
    return getBasketDisplayName(routedBasket);
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Box display="flex" alignItems="center" mb={2}>
        <Button
          startIcon={<InvoicesIcon />}
          onClick={onBackToDashboard || onBack}
          sx={{ textTransform: 'none' }}
        >
          Invoices
        </Button>
        {basketName && (
          <>
            <ChevronRightIcon sx={{ mx: 1, color: 'text.secondary' }} />
            <Button
              onClick={onBackToList || onBack}
              sx={{ textTransform: 'none' }}
            >
              {getBasketDisplayName(basketName)}
            </Button>
          </>
        )}
        <ChevronRightIcon sx={{ mx: 1, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {document?.metadata?.original_filename || document?.document_name || 'Document Details'}
        </Typography>
      </Box>

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" component="h1">
            Document Details
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {document?.metadata?.original_filename || document?.document_name}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="View PDF">
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => setPdfPreviewOpen(true)}
            >
              Preview
            </Button>
          </Tooltip>
          <Tooltip title="Download">
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled
              >
                Download
              </Button>
            </span>
          </Tooltip>
          {document.basket_name === 'raw_invoices' && (
            <Button
              variant="contained"
              startIcon={<ProcessIcon />}
              onClick={() => onProcessDocument(documentId)}
              color="primary"
            >
              Process
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Document Overview */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Document Overview
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <DocumentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  {document?.metadata?.original_filename || document.document_name}
                </Typography>
                <Tooltip title="Open PDF Review">
                  <IconButton 
                    color="primary" 
                    onClick={() => setPdfPreviewOpen(true)}
                    sx={{ 
                      ml: 0.5,
                      backgroundColor: 'primary.light',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.main'
                      }
                    }}
                  >
                    <PdfIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Document ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {documentId}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Current Status
                </Typography>
                <Chip
                  label={processingStatus?.processing_status || 'Unknown'}
                  color={getStatusColor(processingStatus?.processing_status)}
                  size="small"
                />
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Basket
                </Typography>
                <Chip
                  label={document.basket_name?.replace('_', ' ').toUpperCase() || 'Unknown'}
                  color={getBasketColor(document.basket_name)}
                  size="small"
                />
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Routed Basket
                </Typography>
                <Chip
                  label={getRoutedBasketDisplayName(docMetadata)}
                  color={getBasketColor(getRoutedBasketName(docMetadata))}
                  size="small"
                />
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Upload Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(docMetadata?.upload_timestamp)}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  File Size
                </Typography>
                <Typography variant="body1">
                  {docMetadata?.file_size ? `${docMetadata.file_size} bytes` : 'N/A'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Invoice Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Invoice Number
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {docMetadata?.invoice_number || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <MoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(docMetadata?.total_amount, docMetadata?.currency)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Invoice Date
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {formatDate(docMetadata?.invoice_date)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Due Date
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {formatDate(docMetadata?.due_date)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Customer ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {docMetadata?.customer_id || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Supplier ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {docMetadata?.supplier_id || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* PO Validation Rules */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Purchase Order Validation Rules
              </Typography>
              
              {/* Overall Status - Using system data */}
              {(() => {
                // Parse PO validation details if available
                let poValidationDetails = null;
                try {
                  if (docMetadata?.po_validation_details) {
                    poValidationDetails = typeof docMetadata.po_validation_details === 'string' 
                      ? JSON.parse(docMetadata.po_validation_details)
                      : docMetadata.po_validation_details;
                  }
                } catch (e) {
                  console.error('Error parsing PO validation details:', e);
                }

                // Use actual PO validation status from system
                const poValidationStatus = docMetadata?.po_validation_status;
                const confidenceScore = docMetadata?.extraction_confidence || 0;
                const hasValidPayee = docMetadata?.supplier_id || docMetadata?.vendor_id || docMetadata?.payee_name;
                const hasAmount = docMetadata?.total_amount && docMetadata.total_amount !== 'N/A';
                
                
                // Calculate overall status based on detailed validation checks
                const validationChecks = poValidationDetails?.validation_checks || {};
                const systemValidationPassed = poValidationDetails?.is_valid === true || poValidationStatus === 'valid';
                
                
                // Extract amounts for comparison
                const poAmount = poValidationDetails?.validation_details?.total_amount;
                const invoiceAmount = docMetadata?.total_amount;
                
                // Use detailed validation checks for overall status
                const allChecksPass = (
                  confidenceScore >= 0.9 &&
                  validationChecks.po_exists &&
                  validationChecks.supplier_matches &&
                  validationChecks.amount_matches &&
                  validationChecks.po_status_valid &&
                  poValidationDetails?.line_items && poValidationDetails.line_items.length > 0
                );
                
                const overallStatus = allChecksPass ? 'PASSED' : 'FAILED';
                const statusColor = overallStatus === 'PASSED' ? 'success' : 'error';
                const statusIcon = overallStatus === 'PASSED' ? ValidIcon : ErrorIcon;
                // Create detailed status message
                let statusMessage;
                if (overallStatus === 'PASSED') {
                  if (systemValidationPassed) {
                    statusMessage = 'System validation passed - all checks approved';
                  } else {
                    statusMessage = 'All validation rules have passed successfully';
                  }
                } else {
                  const failedChecks = [];
                  if (confidenceScore < 0.9) failedChecks.push('Confidence Score');
                  if (!validationChecks.po_exists) failedChecks.push('PO Exists');
                  if (!validationChecks.supplier_matches) failedChecks.push('Supplier Match');
                  if (!validationChecks.amount_matches) failedChecks.push('Amount Match');
                  if (!validationChecks.po_status_valid) failedChecks.push('PO Status Valid');
                  if (!poValidationDetails?.line_items || poValidationDetails.line_items.length === 0) failedChecks.push('Line Items');
                  
                  statusMessage = `Failed checks: ${failedChecks.join(', ')}`;
                }
                
                return (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2, 
                    mb: 3,
                    backgroundColor: overallStatus === 'PASSED' ? '#f0f8f0' : '#fff5f5',
                    border: `1px solid ${overallStatus === 'PASSED' ? '#d4edda' : '#f8d7da'}`,
                    borderRadius: 1
                  }}>
                    <Box display="flex" alignItems="center">
                      <statusIcon sx={{ mr: 2, color: `${statusColor}.main`, fontSize: 32 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: `${statusColor}.main` }}>
                          Overall Status: {overallStatus}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {statusMessage}
                        </Typography>
                        {poValidationDetails && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            PO: {poValidationDetails.validation_details?.po_number || 'N/A'} | 
                            PO Exists: {validationChecks.po_exists ? 'Yes' : 'No'} | 
                            Amount Match: {validationChecks.amount_matches ? 'Yes' : 'No'} | 
                            Supplier Match: {validationChecks.supplier_matches ? 'Yes' : 'No'} | 
                            PO Status: {validationChecks.po_status_valid ? 'Valid' : 'Invalid'} | 
                            Confidence: {(confidenceScore * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={overallStatus}
                      color={statusColor}
                      size="large"
                      sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                    />
                  </Box>
                );
              })()}
              
              {/* Validation Rules List - Using system data */}
              <Box sx={{ mt: 2 }}>
                {(() => {
                  // Parse PO validation details if available
                  let poValidationDetails = null;
                  try {
                    if (docMetadata?.po_validation_details) {
                      poValidationDetails = typeof docMetadata.po_validation_details === 'string' 
                        ? JSON.parse(docMetadata.po_validation_details)
                        : docMetadata.po_validation_details;
                    }
                  } catch (e) {
                    console.error('Error parsing PO validation details:', e);
                  }

                  const confidenceScore = docMetadata?.extraction_confidence || 0;
                  const poValidationStatus = docMetadata?.po_validation_status;
                  const hasValidPayee = docMetadata?.supplier_id || docMetadata?.vendor_id || docMetadata?.payee_name;
                  const hasAmount = docMetadata?.total_amount && docMetadata.total_amount !== 'N/A';
                  const hasLineItems = docMetadata?.line_items && docMetadata.line_items !== 'N/A';
                  
                  // Extract validation checks from PO validation details if available
                  const validationChecks = poValidationDetails?.validation_checks || {};
                  const poAmount = poValidationDetails?.validation_details?.total_amount;
                  const invoiceAmount = docMetadata?.total_amount;
                  
                  
                  
                  // Check if system validation passed
                  const systemValidationPassed = poValidationDetails?.is_valid === true || poValidationStatus === 'valid';
                  
                  return [
                    {
                      id: 'confidence_score',
                      title: 'Confidence Score',
                      description: 'Confidence score is higher than 0.9',
                      status: confidenceScore >= 0.9 ? 'pass' : 'fail',
                      value: `${(confidenceScore * 100).toFixed(1)}%`
                    },
                    {
                      id: 'po_valid',
                      title: 'PO Validity',
                      description: 'Is PO valid according to system validation?',
                      status: (validationChecks.po_exists || poValidationDetails?.po_exists) ? 'pass' : 'fail',
                      value: poValidationDetails ? 
                        `${poValidationDetails.validation_details?.po_number || 'N/A'} (${(validationChecks.po_exists || poValidationDetails?.po_exists) ? 'Exists' : 'Not Found'})` : 
                        (docMetadata?.related_po || 'N/A')
                    },
                    {
                      id: 'payee_valid',
                      title: 'Payee Validity',
                      description: 'Is Payee valid?',
                      status: (validationChecks.supplier_matches || (poValidationDetails?.validation_details?.supplier_id && docMetadata?.supplier_id && poValidationDetails.validation_details.supplier_id === docMetadata.supplier_id)) ? 'pass' : 'fail',
                      value: poValidationDetails ? 
                        `${poValidationDetails.validation_details?.supplier_name || 'N/A'} (${validationChecks.supplier_matches ? 'Matches' : 'Mismatch'})` :
                        (docMetadata?.supplier_id || docMetadata?.vendor_id || docMetadata?.payee_name || 'N/A')
                    },
                    {
                      id: 'payee_violation',
                      title: 'Payee Violations',
                      description: 'Is Payee has any violation?',
                      status: 'pass', // Assuming no violations from system data
                      value: docMetadata?.payee_violations || 'No violations'
                    },
                    {
                      id: 'amount_match',
                      title: 'Amount Match',
                      description: 'Is Amount match with PO?',
                      status: (validationChecks.amount_matches || (poAmount && invoiceAmount && Math.abs(parseFloat(poAmount) - parseFloat(invoiceAmount)) < 0.01)) ? 'pass' : 'fail',
                      value: poValidationDetails && poAmount && invoiceAmount ? 
                        `Invoice: ${formatCurrency(invoiceAmount, docMetadata.currency)} | PO: ${formatCurrency(poAmount, docMetadata.currency)}` :
                        (docMetadata?.total_amount ? 
                          formatCurrency(docMetadata.total_amount, docMetadata.currency) : 
                          'N/A')
                    },
                    {
                      id: 'terms_correct',
                      title: 'Contract Terms',
                      description: 'Is term correct (i.e., net 30)?',
                      status: 'pass', // Hardcoded as requested
                      value: 'net 30' // Hardcoded as requested
                    },
                    {
                      id: 'line_items_match',
                      title: 'Line Items Match',
                      description: 'Do line items match with PO?',
                      status: (poValidationDetails?.line_items && poValidationDetails.line_items.length > 0) ? 'pass' : 'fail',
                      value: poValidationDetails?.line_items ? 
                        `${poValidationDetails.line_items.length} items in PO` :
                        (docMetadata?.line_items && docMetadata.line_items !== 'N/A' ? 
                          `${Array.isArray(docMetadata.line_items) ? docMetadata.line_items.length : 
                            (typeof docMetadata.line_items === 'string' ? JSON.parse(docMetadata.line_items).length : 0)} items` : 
                          'No items')
                    }
                  ].map((rule, index) => (
                    <Box 
                      key={rule.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2, 
                        mb: 1,
                        backgroundColor: rule.status === 'pass' ? '#f0f8f0' : '#fff5f5',
                        border: `1px solid ${rule.status === 'pass' ? '#d4edda' : '#f8d7da'}`,
                        borderRadius: 1
                      }}
                    >
                      {/* Status Icon */}
                      <Box sx={{ mr: 2 }}>
                        {rule.status === 'pass' ? (
                          <ValidIcon sx={{ color: 'success.main', fontSize: 24 }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
                        )}
                      </Box>
                      
                      {/* Rule Content */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                          {rule.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {rule.description}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'medium',
                          color: rule.status === 'pass' ? 'success.main' : 'error.main'
                        }}>
                          {rule.value}
                        </Typography>
                      </Box>
                      
                      {/* Status Badge */}
                      <Chip
                        label={rule.status === 'pass' ? 'PASS' : 'FAIL'}
                        color={rule.status === 'pass' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  ));
                })()}
              </Box>

              {/* Detailed PO Validation Information */}
              {(() => {
                // Parse PO validation details if available
                let poValidationDetails = null;
                try {
                  if (docMetadata?.po_validation_details) {
                    poValidationDetails = typeof docMetadata.po_validation_details === 'string' 
                      ? JSON.parse(docMetadata.po_validation_details)
                      : docMetadata.po_validation_details;
                  }
                } catch (e) {
                  console.error('Error parsing PO validation details:', e);
                }

                return poValidationDetails && (
                  <Box sx={{ mt: 3 }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <ContractIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">
                        Detailed PO Validation Results
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #dee2e6', 
                      borderRadius: 1,
                      mb: 2
                    }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            PO Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {poValidationDetails.validation_details?.po_number || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            PO Exists
                          </Typography>
                          <Chip
                            label={poValidationDetails.po_exists ? 'Yes' : 'No'}
                            color={poValidationDetails.po_exists ? 'success' : 'error'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            HTTP Status Code
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {poValidationDetails.http_status_code || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Overall Valid
                          </Typography>
                          <Chip
                            label={poValidationDetails.is_valid ? 'Valid' : 'Invalid'}
                            color={poValidationDetails.is_valid ? 'success' : 'error'}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Validation Details */}
                    {poValidationDetails.validation_details && (
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#fff', 
                        border: '1px solid #dee2e6', 
                        borderRadius: 1
                      }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                          Validation Details
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            maxHeight: '300px',
                            overflow: 'auto'
                          }}
                        >
                          <pre style={{ 
                            margin: 0, 
                            fontSize: '12px', 
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {JSON.stringify(poValidationDetails.validation_details, null, 2)}
                          </pre>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Line Item Details */}
              {docMetadata?.line_items && docMetadata.line_items !== 'N/A' && (
                <Box sx={{ mt: 3 }}>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <ListIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      Line Item Details
                    </Typography>
                  </Box>
                  
                  {(() => {
                    let lineItems = docMetadata.line_items;
                    
                    // Handle JSON string format
                    if (typeof lineItems === 'string') {
                      try {
                        lineItems = JSON.parse(lineItems);
                      } catch (e) {
                        console.error('Error parsing line_items JSON:', e);
                        lineItems = null;
                      }
                    }
                    
                    // Ensure it's an array
                    if (!Array.isArray(lineItems)) {
                      lineItems = null;
                    }
                    
                    return lineItems ? (
                      <TableContainer component={Paper} sx={{ maxHeight: '400px', overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Item Code</TableCell>
                              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Description</TableCell>
                              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', textAlign: 'right' }}>Quantity</TableCell>
                              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', textAlign: 'right' }}>Unit Price</TableCell>
                              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', textAlign: 'right' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineItems.map((item, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ fontWeight: 'medium' }}>
                                  {item.item_code || item.sku || item.product_code || 'N/A'}
                                </TableCell>
                                <TableCell sx={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                                  {item.description || item.product_description || 'N/A'}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right' }}>
                                  {item.quantity ? Number(item.quantity).toLocaleString() : 'N/A'}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'medium' }}>
                                  {item.unit_price ? 
                                    formatCurrency(item.unit_price, docMetadata.currency) : 
                                    'N/A'
                                  }
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                                  {item.total || item.line_total ? 
                                    formatCurrency(item.total || item.line_total, docMetadata.currency) : 
                                    'N/A'
                                  }
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 2,
                          borderRadius: 1,
                          maxHeight: '200px',
                          overflow: 'auto'
                        }}
                      >
                        <pre style={{ 
                          margin: 0, 
                          fontSize: '12px', 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {typeof docMetadata.line_items === 'string' ? 
                            docMetadata.line_items : 
                            JSON.stringify(docMetadata.line_items, null, 2)
                          }
                        </pre>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Processing Details */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Typography variant="h6">Processing Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {originalDocumentLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading original document data...
                  </Typography>
                </Box>
              ) : originalDocumentData ? (
                <Grid container spacing={2}>
                  {/* Original Document Info */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Original Document Information
                    </Typography>
                    <Box sx={{ mb: 2, p: 2, backgroundColor: '#f0f8ff', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Original Document ID: {originalDocumentData.originalDocId}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Basic Processing Info from Original Document */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Extraction Confidence
                    </Typography>
                    <Typography variant="body1">
                      {originalDocumentData.metadata?.extraction_confidence ? 
                        `${(parseFloat(originalDocumentData.metadata.extraction_confidence) * 100).toFixed(1)}%` : 
                        'N/A'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Extraction Method
                    </Typography>
                    <Typography variant="body1">
                      {originalDocumentData.metadata?.extraction_method || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Processing Pipeline Version
                    </Typography>
                    <Typography variant="body1">
                      {originalDocumentData.metadata?.processing_pipeline_version || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Ready for Routing
                    </Typography>
                    <Chip
                      label={originalDocumentData.metadata?.ready_for_routing === '1' ? 'Yes' : 'No'}
                      color={originalDocumentData.metadata?.ready_for_routing === '1' ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>


                  {/* LLM Agent Response from Original Document */}
                  {(() => {
                    // Check if we have any LLM-related data
                    const hasLLMData = originalDocumentData.metadata?.llm_raw_response || 
                                     docMetadata?.llm_raw_response ||
                                     originalDocumentData.metadata?.['Openai Raw Response'] || 
                                     docMetadata?.['Openai Raw Response'] ||
                                     originalDocumentData.metadata?.['openai_raw_response'] || 
                                     docMetadata?.['openai_raw_response'] ||
                                     originalDocumentData.metadata?.llm_model || 
                                     docMetadata?.llm_model ||
                                     originalDocumentData.metadata?.model || 
                                     docMetadata?.model ||
                                     originalDocumentData.metadata?.extraction_method || 
                                     docMetadata?.extraction_method;
                    
                    return hasLLMData;
                  })() && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="h6">
                          LLM Agent Response
                        </Typography>
                        <Button
                          size="small"
                          startIcon={expandedLLMResponses['original-llm'] ? <ExpandLessIcon /> : <ExpandIcon />}
                          onClick={() => toggleLLMResponseExpansion('original-llm')}
                          sx={{ textTransform: 'none' }}
                        >
                          {expandedLLMResponses['original-llm'] ? 'Collapse' : 'Expand'}
                        </Button>
                      </Box>
                      
                      {/* LLM Response Metadata */}
                      <Box sx={{ mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Model Used
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {(() => {
                                const rawResponse = originalDocumentData.metadata?.llm_raw_response || 
                                                  docMetadata?.llm_raw_response ||
                                                  originalDocumentData.metadata?.['Openai Raw Response'] || 
                                                  docMetadata?.['Openai Raw Response'] ||
                                                  originalDocumentData.metadata?.['openai_raw_response'] || 
                                                  docMetadata?.['openai_raw_response'];
                                
                                return extractModelFromRawResponse(rawResponse) ||
                                       originalDocumentData.metadata?.llm_model || 
                                       docMetadata?.llm_model || 
                                       originalDocumentData.metadata?.model || 
                                       docMetadata?.model || 
                                       'N/A';
                              })()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Execution Time
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {originalDocumentData.metadata?.llm_execution_time || 
                               docMetadata?.llm_execution_time || 
                               originalDocumentData.metadata?.execution_time || 
                               docMetadata?.execution_time || 
                               'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Tokens Used
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {(() => {
                                const rawResponse = originalDocumentData.metadata?.llm_raw_response || 
                                                  docMetadata?.llm_raw_response ||
                                                  originalDocumentData.metadata?.['Openai Raw Response'] || 
                                                  docMetadata?.['Openai Raw Response'] ||
                                                  originalDocumentData.metadata?.['openai_raw_response'] || 
                                                  docMetadata?.['openai_raw_response'];
                                
                                const usage = extractUsageFromRawResponse(rawResponse);
                                const totalTokens = usage?.total_tokens;
                                
                                return totalTokens ? `${totalTokens} (${usage.completion_tokens || 0} completion, ${usage.prompt_tokens || 0} prompt)` :
                                       originalDocumentData.metadata?.llm_tokens_used || 
                                       docMetadata?.llm_tokens_used || 
                                       originalDocumentData.metadata?.tokens_used || 
                                       docMetadata?.tokens_used || 
                                       'N/A';
                              })()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Processing Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {formatDate(originalDocumentData.metadata?.llm_processing_date || 
                                         docMetadata?.llm_processing_date || 
                                         originalDocumentData.metadata?.processing_date || 
                                         docMetadata?.processing_date)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Confidence Score
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {originalDocumentData.metadata?.llm_confidence || 
                               docMetadata?.llm_confidence || 
                               originalDocumentData.metadata?.confidence || 
                               docMetadata?.confidence || 
                               'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Temperature
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {originalDocumentData.metadata?.llm_temperature || 
                               docMetadata?.llm_temperature || 
                               originalDocumentData.metadata?.temperature || 
                               docMetadata?.temperature || 
                               'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Prompt Version
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {originalDocumentData.metadata?.llm_prompt_version || 
                               docMetadata?.llm_prompt_version || 
                               originalDocumentData.metadata?.prompt_version || 
                               docMetadata?.prompt_version || 
                               'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              API Provider
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {originalDocumentData.metadata?.llm_provider || 
                               docMetadata?.llm_provider || 
                               originalDocumentData.metadata?.provider || 
                               docMetadata?.provider || 
                               'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Raw LLM Response */}
                      {(() => {
                        const rawResponse = originalDocumentData.metadata?.llm_raw_response || 
                                          docMetadata?.llm_raw_response ||
                                          originalDocumentData.metadata?.['Openai Raw Response'] || 
                                          docMetadata?.['Openai Raw Response'] ||
                                          originalDocumentData.metadata?.['openai_raw_response'] || 
                                          docMetadata?.['openai_raw_response'];
                        
                        return rawResponse && (
                          <Box
                            sx={{
                              backgroundColor: '#e8f5e8',
                              p: 2,
                              borderRadius: 1,
                              maxHeight: expandedLLMResponses['original-llm'] ? 'none' : '400px',
                              overflow: 'auto'
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                              Raw LLM Response:
                            </Typography>
                            <pre style={{ 
                              margin: 0, 
                              fontSize: '12px', 
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {formatLLMResponse(rawResponse)}
                            </pre>
                          </Box>
                        );
                      })()}
                    </Grid>
                  )}

                  {/* Original Document Metadata Display */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Original Document Metadata
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#f5f5f5',
                        p: 2,
                        borderRadius: 1,
                        maxHeight: '400px',
                        overflow: 'auto'
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Field</strong></TableCell>
                            <TableCell><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(originalDocumentData.metadata || {}).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 'medium', minWidth: '200px' }}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </TableCell>
                              <TableCell sx={{ wordBreak: 'break-word' }}>
                                {formatMetadataValue(value)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>

                  {/* Raw Responses from Original Document */}
                  {originalDocumentData.rawResponses && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="h6">
                          Raw Processing Responses
                        </Typography>
                        <Button
                          size="small"
                          startIcon={expandedRawResponses['original-raw'] ? <ExpandLessIcon /> : <ExpandIcon />}
                          onClick={() => toggleRawResponseExpansion('original-raw')}
                          sx={{ textTransform: 'none' }}
                        >
                          {expandedRawResponses['original-raw'] ? 'Collapse' : 'Expand'}
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: '#e3f2fd',
                          p: 2,
                          borderRadius: 1,
                          maxHeight: expandedRawResponses['original-raw'] ? 'none' : '400px',
                          overflow: 'auto'
                        }}
                      >
                        <pre style={{ 
                          margin: 0, 
                          fontSize: '12px', 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {formatLLMResponse(originalDocumentData.rawResponses)}
                        </pre>
                      </Box>
                    </Grid>
                  )}

                </Grid>
              ) : (
                <Grid container spacing={2}>
                  {/* Fallback to current document data if no original document */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This appears to be an original document. Processing details are shown below.
                    </Typography>
                  </Grid>

                  {/* Basic Processing Info */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Extraction Confidence
                    </Typography>
                    <Typography variant="body1">
                      {docMetadata?.extraction_confidence ? 
                        `${(parseFloat(docMetadata.extraction_confidence) * 100).toFixed(1)}%` : 
                        'N/A'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Extraction Method
                    </Typography>
                    <Typography variant="body1">
                      {docMetadata?.extraction_method || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Processing Pipeline Version
                    </Typography>
                    <Typography variant="body1">
                      {docMetadata?.processing_pipeline_version || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Ready for Routing
                    </Typography>
                    <Chip
                      label={docMetadata?.ready_for_routing === '1' ? 'Yes' : 'No'}
                      color={docMetadata?.ready_for_routing === '1' ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>


                  {/* All Metadata Display */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Complete Metadata
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#f5f5f5',
                        p: 2,
                        borderRadius: 1,
                        maxHeight: '400px',
                        overflow: 'auto'
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Field</strong></TableCell>
                            <TableCell><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(docMetadata || {}).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 'medium', minWidth: '200px' }}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </TableCell>
                              <TableCell sx={{ wordBreak: 'break-word' }}>
                                {formatMetadataValue(value)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

      </Grid>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={() => {
          setPdfPreviewOpen(false);
          if (onPdfPreviewClose) onPdfPreviewClose();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              PDF Preview - {document?.metadata?.original_filename || document.document_name}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled
              >
                Download
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setPdfPreviewOpen(false);
                  if (onPdfPreviewClose) onPdfPreviewClose();
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              height: '70vh',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f5f5f5'
            }}
          >
            {/* PDF Viewer */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                m: 2,
                borderRadius: 1,
                overflow: 'auto'
              }}
            >
              {pdfLoading && (
                <Box textAlign="center">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading PDF...
                  </Typography>
                </Box>
              )}
              
              {pdfError && (
                <Box textAlign="center">
                  <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    PDF Load Error
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {pdfError}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Document: {document?.metadata?.original_filename || document.document_name}
                  </Typography>
                </Box>
              )}
              
              {pdfUrlLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading PDF...
                  </Typography>
                </Box>
              )}
              
              {!pdfUrlLoading && !pdfLoading && !pdfError && pdfBlobUrl && (
                <Box
                  sx={{
                    width: '100%',
                    height: '600px',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <iframe
                    src={pdfBlobUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    onLoad={handlePdfLoad}
                    onError={handlePdfError}
                    title="PDF Document"
                  />
                </Box>
              )}
            </Box>
            
            {/* Simple PDF Info */}
            <Box
              sx={{
                p: 2,
                backgroundColor: 'white',
                borderTop: '1px solid #ddd',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                PDF Document Viewer - Use browser controls to navigate
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentDetails;
